/* Phase 54 — Premium Gallery Lightbox
 *
 * Reusable full-screen image viewer with:
 *  - keyboard navigation (← / → / Esc)
 *  - touch swipe (mobile)
 *  - thumbnail strip at the bottom
 *  - smooth cross-fade between slides
 *  - image counter (3 / 18)
 *  - body-scroll lock while open
 *  - lazy-loads non-active images
 *
 * Used by ListingDetail. Props:
 *   - images: string[] (storage paths, will be resolved via api.fileUrl)
 *   - open:   bool
 *   - index:  number (initial slide)
 *   - title:  string (for alt + a11y)
 *   - onClose: () => void
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";
import { api } from "../lib/api";

const SWIPE_THRESHOLD = 50;

export default function GalleryLightbox({ images, open, index = 0, title = "", label = "Gallery", onClose }) {
  const [active, setActive] = useState(index);
  const touchStartX = useRef(null);
  const thumbsRef = useRef(null);

  // Sync active with prop changes when re-opened on a different image.
  useEffect(() => {
    if (open) setActive(index);
  }, [open, index]);

  // Body scroll lock + keyboard listener while open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") setActive((i) => (i - 1 + images.length) % images.length);
      else if (e.key === "ArrowRight") setActive((i) => (i + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, images.length, onClose]);

  // Auto-scroll the active thumbnail into view.
  useEffect(() => {
    if (!open || !thumbsRef.current) return;
    const el = thumbsRef.current.querySelector(`[data-thumb-idx="${active}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [active, open]);

  const prev = useCallback(() => setActive((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setActive((i) => (i + 1) % images.length), [images.length]);

  if (!open || !images || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-black/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} – ${label}`}
      data-testid="gallery-lightbox"
    >
      {/* Top bar — Phase 66: safe-area inset for iOS Dynamic Island / notch */}
      <div
        className="flex items-center justify-between px-4 py-3 sm:px-6"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <span className="font-display text-sm font-semibold text-white sm:text-base" data-testid="gallery-counter">
          {active + 1} <span className="text-zinc-500">/ {images.length}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schliessen"
          className="grid h-12 w-12 place-items-center rounded-full bg-zinc-900/80 text-white transition hover:bg-zinc-800 active:scale-95"
          data-testid="gallery-close"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Main slide area */}
      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden"
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(dx) > SWIPE_THRESHOLD) {
            if (dx > 0) prev();
            else next();
          }
          touchStartX.current = null;
        }}
      >
        {/* Image */}
        <img
          key={active}
          src={api.fileUrl(images[active])}
          alt={`${title} – Bild ${active + 1}`}
          className="max-h-full max-w-full animate-[fade-in_0.25s_ease-out] select-none object-contain px-2 sm:px-6"
          draggable={false}
          data-testid="gallery-active-image"
        />

        {/* Prev / Next buttons (hidden on tiny screens — touch swipe replaces them) */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Vorheriges Bild"
              className="absolute left-2 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-zinc-900/70 text-white transition hover:bg-zinc-800 sm:grid sm:left-4"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Nächstes Bild"
              className="absolute right-2 top-1/2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-zinc-900/70 text-white transition hover:bg-zinc-800 sm:grid sm:right-4"
              data-testid="gallery-next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="border-t border-zinc-900/80 bg-black/40">
          <div
            ref={thumbsRef}
            className="flex gap-1.5 overflow-x-auto px-4 py-3 sm:gap-2 sm:px-6 sm:py-4"
            data-testid="gallery-thumbs"
          >
            {images.map((p, i) => (
              <button
                key={p}
                type="button"
                data-thumb-idx={i}
                onClick={() => setActive(i)}
                className={`relative h-14 w-20 flex-shrink-0 overflow-hidden rounded-md border-2 transition sm:h-16 sm:w-24 ${
                  active === i
                    ? "border-red-500 opacity-100"
                    : "border-zinc-800 opacity-50 hover:opacity-100"
                }`}
                aria-label={`Bild ${i + 1} anzeigen`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img
                  src={api.fileUrl(p)}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact inline gallery trigger used on listing-detail and card hover. */
export function GalleryHeroButton({ count, label, onClick }) {
  if (!count || count < 2) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition hover:bg-black/90"
      data-testid="gallery-open-trigger"
    >
      <Maximize2 className="h-3.5 w-3.5" />
      {label || `${count} photos`}
    </button>
  );
}
